import { ButtonGroup, Stack } from "@mui/material";
import { z } from "zod/v4";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Form, FormProps, useFormContext, useStore } from "@/components/form";

const editMonedasFormDetailSchema = z.object({
    id: z.string().optional(),
    nombre: z.string().nonempty("Este campo es requerido"),
    simbolo: z.string().nonempty("Este campo es requerido"),
    hidden: z.boolean().default(false),
    monedaBase: z.boolean().optional()
});

export const editMonedasFormSchema = z.object({
    details: z.array(editMonedasFormDetailSchema),
})

export default function EditMonedasForm({
    form,
    ...props
}: FormProps<z.input<typeof editMonedasFormSchema>>) {
    return <Form form={form as any} gap={3} {...props}>
        <form.Field name="details" mode="array">
            {field => <>
                {field.state.value.map((m, i) => m.monedaBase ? <MonedaPrincipalField key={i} index={i} /> :
                    m.id ? <MonedaSecundariaField key={i} index={i} /> :
                        <MonedaRemovableField key={i} index={i} onRemove={() => field.removeValue(i)} />
                )}
                <Form.Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => field.pushValue({ nombre: "", simbolo: "", hidden: false })}
                >Nueva Moneda</Form.Button>
            </>}
        </form.Field>
        <Form.SubmitButton>Guardar</Form.SubmitButton>
    </Form>
}

interface MonedaFieldProps {
    index: number,
}

function MonedaPrincipalField({ index }: MonedaFieldProps) {
    return <Stack direction={"row"} gap={2} alignItems={"center"}>
        <Stack flex={2}>
            <Form.TextField name={`details[${index}].nombre`} label="Moneda Principal" />
        </Stack>
        <Stack flex={0.5} minWidth={"100px"}>
            <Form.TextField name={`details[${index}].simbolo`} label="Símbolo" />
        </Stack>
    </Stack>
}

function MonedaSecundariaField({ index }: MonedaFieldProps) {
    const form = useFormContext();
    const moneda = useStore(form.store, state => state.values.details[index])

    return <Stack direction={"row"} gap={2} alignItems={"center"}>
        <SortOrderButtons index={index} />
        <Stack flex={2}>
            <Form.TextField name={`details[${index}].nombre`} label="Moneda" disabled={moneda.hidden} />
        </Stack>
        <Stack flex={0.5} minWidth={"100px"}>
            <Form.TextField name={`details[${index}].simbolo`} label="Símbolo" disabled={moneda.hidden} />
        </Stack>
        <Stack>
            <Form.IconButton onClick={() => form.setFieldValue(`details[${index}].hidden`, !moneda.hidden)}>
                {!moneda.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </Form.IconButton>
        </Stack>
    </Stack>
}

interface MonedaRemovableFieldProps extends MonedaFieldProps {
    onRemove: () => void
}

function MonedaRemovableField({ index, onRemove }: MonedaRemovableFieldProps) {
    return <Stack direction={"row"} gap={2} alignItems={"center"}>
        <SortOrderButtons index={index} />
        <Stack flex={2}>
            <Form.TextField name={`details[${index}].nombre`} label="Nueva Moneda" />
        </Stack>
        <Stack flex={0.5} minWidth={"100px"}>
            <Form.TextField name={`details[${index}].simbolo`} label="Símbolo" />
        </Stack>
        <Stack>
            <Form.IconButton onClick={onRemove}>
                <CloseIcon />
            </Form.IconButton>
        </Stack>
    </Stack>
}

interface SortOrderButtonsProps {
    index: number
}

function SortOrderButtons({
    index
}: SortOrderButtonsProps) {
    const form = useFormContext();

    const details = useStore(form.store, state => state.values.details);

    const handleUpClick = () => {
        if (index - 1 >= 1) form.moveFieldValues("details", index, index - 1)
    }

    const handleDownClick = () => {
        if (index + 1 < details.length) form.moveFieldValues("details", index, index + 1)
    }

    return <Stack>
        <ButtonGroup orientation="vertical" size="small" variant="text" color="inherit" >
            <Form.Button size="small" onClick={handleUpClick} sx={{ width: "30px" }} disabled={index - 1 < 1} >
                <ArrowDropUpIcon fontSize="small" />
            </Form.Button>
            <Form.Button size="small" onClick={handleDownClick} sx={{ width: "30px" }} disabled={index + 1 >= details.length}>
                <ArrowDropDownIcon fontSize="small" />
            </Form.Button>
        </ButtonGroup>
    </Stack>
}